<!--
  THE ALERT BELL in the top bar (Mark, 2026-09-27: "I want to make sure that the
  dashboard ALSO shows alerts like these").

  "These" is the email he got from a real truck: a stored diagnostic code. That
  matters, because the product already had an open-faults list and it would have
  shown him NOTHING -- `openFaults` reads `kind = 'fault'` only, and a truck
  raises `dtc_set`. This bell reads `GET /vehicle/alerts`, whose rule is the same
  `isNotifiable` the emailer uses, so the bell shows what the email shows. The
  service's own header argues that at length.

  WHY IT POLLS RATHER THAN LISTENING. There IS an SSE stream carrying
  `vehicle_event` frames, and the tracking board holds one. Putting a second
  EventSource in the SHELL would give every open tab on every page its own
  connection, roughly doubling the server's stream count for a badge that
  changes a few times a day; the host's measured ceiling is about 2,000
  concurrent streams. A bounded query on a slow timer is the cheaper trade, and
  the refetch after clearing an alert is what makes the common case feel
  immediate. If the badge ever needs to be live to the second, the stream is
  where to take it, not a faster timer.

  THE PANEL IS A POPOVER, NOT A MENU. LwMenu is built for a list of commands;
  these rows are records with two actions each, so this component owns its own
  disclosure -- but it keeps LwMenu's contract: Escape closes, a click outside
  closes, focus returns to the bell, and `aria-expanded`/`aria-controls` say what
  is going on.
-->
<template>
  <div ref="root" class="lw-p-alert-bell">
    <button
      ref="trigger"
      type="button"
      class="lw-p-alert-bell__trigger"
      :aria-expanded="String(open)"
      aria-controls="lw-alert-panel"
      :aria-label="triggerLabel"
      @click="toggle"
    >
      <i class="fa fa-bell" aria-hidden="true" />
      <!--
        The count, and NOT a bare dot: "3" is a different decision from "1", and
        a dispatcher deciding whether to look now deserves the number. Capped at
        9+ because a two-digit badge on a 3rem bar stops being a badge.
        aria-hidden because triggerLabel already says it in words -- a screen
        reader hearing "Alerts 3 3" is worse than hearing it once.
      -->
      <span v-if="count > 0" class="lw-p-alert-bell__badge" aria-hidden="true">
        {{ count > 9 ? '9+' : count }}
      </span>
    </button>

    <div
      v-if="open"
      id="lw-alert-panel"
      class="lw-p-alert-bell__panel"
      role="group"
      aria-label="Vehicle alerts"
    >
      <div class="lw-p-alert-bell__panel-head">
        <h2 class="lw-p-alert-bell__panel-title">Vehicle alerts</h2>
        <button
          type="button"
          class="lw-p-alert-bell__close"
          aria-label="Close alerts"
          @click="close"
        >
          <i class="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <p v-if="workOrderNotice" class="lw-p-alert-bell__status" role="status">
        {{ workOrderNotice }}
      </p>

      <p v-if="loading" class="lw-p-alert-bell__status" aria-busy="true">Loading alerts…</p>

      <p v-else-if="failed" class="lw-p-alert-bell__status">
        Alerts could not be loaded.
        <LwButton variant="link" size="sm" @click="fetchAlerts">Retry</LwButton>
      </p>

      <!--
        An empty bell says so in words rather than showing an empty box. "Nothing
        to deal with" is a useful answer, and the second sentence stops it being
        read as "alerting is broken".
      -->
      <div v-else-if="alerts.length === 0" class="lw-p-alert-bell__empty">
        <p class="lw-p-alert-bell__empty-title">No open alerts</p>
        <p class="lw-p-alert-bell__status">
          Faults your trucks report appear here, and are emailed to the people who have asked for
          them.
        </p>
      </div>

      <ul v-else class="lw-p-alert-bell__list">
        <li v-for="alert in alerts" :key="alert.id" class="lw-p-alert-bell__item">
          <p class="lw-p-alert-bell__subject">{{ subjectOf(alert) }}</p>
          <p class="lw-p-alert-bell__fault">{{ alert.description }}</p>
          <p class="lw-p-alert-bell__meta">
            {{ whenOf(alert) }}
            <template v-if="alert.occurrences > 1"> · {{ alert.occurrences }} times </template>
            <!--
              Said outright when it is NOT true: a dispatcher looking at an alert
              nobody was emailed about should know that, because it is usually a
              sign the fault arrived under the six-hourly hysteresis rule rather
              than that mail is broken.
            -->
            <template v-if="!alert.emailed_at"> · not emailed </template>
          </p>
          <!--
            LwButton variant="link", not a hand-styled <button>: the token-purity
            gate covers components/header, and its rule for this phase is that
            nothing may add a new lw-* copy-paste block. A link-looking button
            already exists as a variant.
          -->
          <div class="lw-p-alert-bell__actions">
            <LwButton v-if="alert.truck_id" variant="link" size="sm" @click="openTruck(alert)">
              Open truck
            </LwButton>
            <!--
              Spec 38 §1.5: the fault's work order, for the people who can open
              one (fleet.manage). It carries the fault's id and its DTC text;
              the alert itself stays until it is cleared, because a work order
              is not a repair.
            -->
            <LwButton
              v-if="canManageFleet && alert.truck_id"
              variant="link"
              size="sm"
              :data-test="`alert-work-order-${alert.id}`"
              @click="openWorkOrder(alert)"
            >
              Create work order
            </LwButton>
            <LwButton variant="link" size="sm" @click="promptClear(alert)"> Clear… </LwButton>
          </div>
        </li>
      </ul>
    </div>

    <!--
      CLEARING ASKS WHY (Mark: "probably ask for a note when clearing alerts"),
      and takes an empty answer. The migration and the controller both argue it:
      this button is what resets the six-hourly reminder, so nagging here makes a
      dispatcher stop pressing it and start getting an email every six hours
      about a fault they already know about.
    -->
    <LwDialog
      :open="clearing !== null"
      title="Clear this alert"
      :description="clearDescription"
      confirm-label="Clear alert"
      :busy="clearBusy"
      @confirm="confirmClear"
      @cancel="cancelClear"
    >
      <LwField
        v-model="clearNote"
        label="Note (optional)"
        description="What you know about it — “known issue, in the shop Tuesday”. Left blank, the alert
          is recorded as cleared with no reason given."
        type="textarea"
        :disabled="clearBusy"
        :error="clearError"
      />
      <p class="lw-p-alert-bell__status">
        Clearing records that somebody has seen this and stops the reminder email. It does not fix
        the truck, and only the vehicle itself can clear the fault.
      </p>
    </LwDialog>

    <WorkOrderDialog
      v-if="canManageFleet"
      :open="workOrderFor !== null"
      :truck-id="workOrderFor ? String(workOrderFor.truck_id) : null"
      :unit-label="workOrderFor ? subjectOf(workOrderFor) : null"
      :prefill="workOrderPrefill"
      @close="workOrderFor = null"
      @saved="workOrderSaved"
    />
  </div>
</template>

<script>
import LwButton from '../lw/LwButton.vue';
import LwDialog from '../lw/LwDialog.vue';
import LwField from '../lw/LwField.vue';
import { ageLabel } from '../../pages/Tracking/lib/time.js';
import WorkOrderDialog from '../fleet/WorkOrderDialog.vue';
import { canManageFleet } from '../fleet/fleetFormat';

/**
 * How often the badge re-reads. A fault is not a chat message: a minute late is
 * not late, and this runs on every page for every open tab (see the header for
 * why it is not an SSE stream).
 */
const POLL_MS = 60000;

export default {
  name: 'AlertBell',
  components: { LwButton, LwDialog, LwField, WorkOrderDialog },
  data() {
    return {
      open: false,
      loading: false,
      failed: false,
      alerts: [],
      count: 0,
      now: Date.now(),
      timer: null,
      clearing: null,
      clearNote: '',
      clearBusy: false,
      clearError: null,
      workOrderFor: null,
      workOrderNotice: null,
    };
  },
  computed: {
    canManageFleet() {
      return canManageFleet(this.$store);
    },
    workOrderPrefill() {
      const a = this.workOrderFor;
      if (!a) return null;
      return {
        opened_from: 'fault_event',
        vehicle_event_id: String(a.id),
        description: a.description || 'Vehicle fault',
      };
    },
    triggerLabel() {
      if (this.count === 0) return 'Vehicle alerts, none open';
      return `Vehicle alerts, ${this.count} open`;
    },
    clearDescription() {
      if (!this.clearing) return null;
      return `${this.subjectOf(this.clearing)} — ${this.clearing.description}`;
    },
  },
  created() {
    // The COUNT is fetched whether or not anybody opens the panel: the badge is
    // the whole point, and a bell that only counted once you clicked it would
    // never tell anybody anything.
    this.fetchAlerts();
    this.timer = setInterval(() => {
      this.now = Date.now();
      this.fetchAlerts();
    }, POLL_MS);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick, true);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick, true);
  },
  methods: {
    fetchAlerts() {
      // Not `loading` on a background refresh: the panel must not flash a
      // spinner over a list somebody is reading every time the timer fires.
      if (this.alerts.length === 0 && !this.failed) this.loading = true;
      return this.$http
        .get('/vehicle/alerts')
        .then(res => {
          const body = res.data || {};
          this.alerts = Array.isArray(body.alerts) ? body.alerts : [];
          this.count = typeof body.count === 'number' ? body.count : this.alerts.length;
          this.failed = false;
          this.loading = false;
        })
        .catch(() => {
          // The BADGE is left alone on a failure: a network blip must not read
          // as "your alerts went away". Only the panel says anything.
          this.failed = true;
          this.loading = false;
        });
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.fetchAlerts();
    },
    close() {
      if (!this.open) return;
      this.open = false;
      // Focus goes back where it came from, the contract LwMenu and LwDialog
      // both keep.
      if (this.$refs.trigger) this.$refs.trigger.focus();
    },
    onKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.open &&
        this.clearing === null &&
        this.workOrderFor === null
      ) {
        this.close();
      }
    },
    onDocumentClick(event) {
      if (!this.open || this.clearing !== null || this.workOrderFor !== null) return;
      const root = this.$refs.root;
      if (root && !root.contains(event.target)) this.open = false;
    },
    subjectOf(alert) {
      if (alert.truck_unit_number) return `Truck ${alert.truck_unit_number}`;
      return alert.device_label || 'An unbonded device';
    },
    whenOf(alert) {
      return ageLabel(alert.occurred_at, this.now);
    },
    openTruck(alert) {
      this.close();
      // The detail view: somebody following an alert wants to SEE the truck --
      // its readings, its open faults and what changed recently -- not a form.
      this.$router.push({ name: 'TrucksView', params: { id: String(alert.truck_id) } });
    },
    openWorkOrder(alert) {
      this.workOrderNotice = null;
      this.workOrderFor = alert;
    },
    workOrderSaved({ message } = {}) {
      this.workOrderFor = null;
      this.workOrderNotice = message || 'Work order created.';
    },
    promptClear(alert) {
      this.clearing = alert;
      this.clearNote = '';
      this.clearError = null;
    },
    cancelClear() {
      this.clearing = null;
      this.clearNote = '';
      this.clearError = null;
      this.clearBusy = false;
    },
    confirmClear() {
      if (!this.clearing || this.clearBusy) return undefined;
      const alert = this.clearing;
      this.clearBusy = true;
      this.clearError = null;
      return this.$http
        .post(`/vehicle/faults/${alert.id}/ack`, { note: this.clearNote })
        .then(() => {
          this.clearBusy = false;
          this.clearing = null;
          this.clearNote = '';
          // Refetched rather than spliced out locally: the server decides what
          // is still open, and a second dispatcher may have cleared others in
          // the meantime. This is also why the poll can be slow.
          return this.fetchAlerts();
        })
        .catch(err => {
          this.clearBusy = false;
          const errors = err && err.response && err.response.data && err.response.data.errors;
          this.clearError =
            (errors && errors.note && errors.note[0]) || 'This could not be cleared.';
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-p-alert-bell {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

// The bar is the chassis blue now (app.scss), so the trigger's ink is the
// inverse ramp and its hover is a white tint -- the opposite of what this
// control needed when the bar was white.
.lw-p-alert-bell__trigger {
  position: relative;
  border: 0;
  background: transparent;
  color: var(--lw-fg-inverse);
  cursor: pointer;
  font: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 100%;
  padding: 0;

  // The same fill a hovered nav item takes on the chassis rail
  // (navLink.vue), so the bar and the sidenav behave as one surface. A white
  // tint would be a raw colour literal, which the token-purity gate refuses
  // for good reason: it is how a palette ends up declared nowhere.
  &:hover {
    background-color: var(--lw-navy-950);
  }

  @include lw-focus-visible;
}

.lw-p-alert-bell__badge {
  position: absolute;
  top: 0.45rem;
  right: 0.55rem;
  min-width: 1.05rem;
  height: 1.05rem;
  padding: 0 0.2rem;
  border-radius: 999px;
  // The product's destructive fill, exactly as LwButton's destructive variant
  // pairs it: --lw-reg-600 ground, inverse ink.
  background: var(--lw-reg-600);
  color: var(--lw-fg-inverse);
  font: var(--lw-type-micro);
  line-height: 1.05rem;
  text-align: center;
  // A ring in the bar's own colour, so the badge reads as sitting ON the bell
  // rather than merging into it at small sizes.
  box-shadow: 0 0 0 2px var(--lw-bg-chassis);
}

.lw-p-alert-bell__panel {
  position: absolute;
  top: 100%;
  right: 0;
  // Above the page, below a dialog: the clear-with-note dialog opens FROM this
  // panel and must sit over it, and --lw-z-dialog is higher.
  z-index: var(--lw-z-overlay);
  width: 22rem;
  max-width: calc(100vw - var(--lw-space-4));
  max-height: 70vh;
  overflow-y: auto;
  background: var(--lw-bg-paper);
  color: var(--lw-fg-primary);
  border: 1px solid var(--lw-line-hairline);
  border-radius: var(--lw-radius-md);
  box-shadow: var(--lw-elev-3-shadow);
}

.lw-p-alert-bell__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lw-space-2);
  padding: var(--lw-space-3) var(--lw-space-4);
  border-bottom: 1px solid var(--lw-line-hairline);
}

.lw-p-alert-bell__panel-title {
  margin: 0;
  font: var(--lw-type-subhead);
}

.lw-p-alert-bell__close {
  border: 0;
  background: transparent;
  color: var(--lw-fg-secondary);
  cursor: pointer;
  font: inherit;
  padding: var(--lw-space-1);

  @include lw-focus-visible;
}

.lw-p-alert-bell__status {
  margin: 0;
  padding: var(--lw-space-3) var(--lw-space-4);
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-p-alert-bell__empty {
  padding-bottom: var(--lw-space-2);
}

.lw-p-alert-bell__empty-title {
  margin: 0;
  padding: var(--lw-space-4) var(--lw-space-4) 0;
  font: var(--lw-type-subhead);
}

.lw-p-alert-bell__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.lw-p-alert-bell__item {
  padding: var(--lw-space-3) var(--lw-space-4);
  border-bottom: 1px solid var(--lw-line-hairline);

  &:last-child {
    border-bottom: 0;
  }
}

.lw-p-alert-bell__subject {
  margin: 0;
  font: var(--lw-type-label);
}

.lw-p-alert-bell__fault {
  margin: var(--lw-space-1) 0 0;
  font: var(--lw-type-body-sm);
}

.lw-p-alert-bell__meta {
  margin: var(--lw-space-1) 0 0;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-alert-bell__actions {
  display: flex;
  gap: var(--lw-space-3);
  margin-top: var(--lw-space-2);
}
</style>
